import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Flex, ListBox, Item, View, ProgressCircle, Link } from '@adobe/react-spectrum';
import { CircleFill, ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import { statusColor, truncateText } from '../../../../../utils/helperUtil';
import { useViewInfo } from '../../../../../hooks/UseViewInfo';
import { useDependency } from '../../../../../contexts/DependencyProvider';
import { ListStatusRequest, ListStatusType } from '../../../../../services/soap/project/requests/ListStatusRequest';
import { ListRegisterRequest } from '../../../../../services/soap/features/requests/ListRegisterRequest';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../../../state/store';
import { useSelector } from 'react-redux';
import styles from './activity_status_filter_component.module.css';

interface ActivityStatusFilterComponentProps {
  createParams: (filterType: any, filterValue: any) => void;
  filterParams?: any;
}

const ActivityStatusFilterComponent: React.FC<ActivityStatusFilterComponentProps> = ({ createParams, filterParams }) => {
  const selectedApp = useSelector((state: RootState) => state.userWebLayout.selectedAppId);
  const { isMobile } = useViewInfo();
  const { t } = useTranslation();
  const { store, projectService, featureService } = useDependency();
  const [toggleListVisibility, setToggleListVisibility] = useState<any>({ stateList: true });
  const [stateSelectedKey, setStateSelectedKey] = useState<Set<string>>(new Set(['All states']));
  const activityStatusList = useRef<any>([]);
  const [showLoader, setShowLoader] = useState<boolean>(true);

  useEffect(() => {
    (async () => {
      await getActivityStatusArray();
    })();
  }, [selectedApp]);

  const getActivityStatusArray = useCallback(async () => {
    try {
      let resListRegister = await featureService.listRegister(new ListRegisterRequest(store.Server, store.SessionId, undefined, undefined, undefined, undefined, +selectedApp));
      let serviceActivityValue = resListRegister?.ROWS?.find((row: any) => row.ROW.key === 'FPATypeCategoryForListActivity')?.ROW.value ?? '';

      const statusArray: any = [];
      let res: any = await projectService.listActivityStatus2(new ListStatusRequest(store.Server, store.SessionId, ListStatusType.Activity, serviceActivityValue));

      if (res.result === 'OK' && res.STATUS.length > 0) {
        //debugger
        res?.STATUS.forEach((status: any) => {
          // Extract id and name from each STATUS object
          const { id, name, color } = status;
          let colorString = statusColor(color);
          // Push an object containing id and name to the statusArray
          statusArray.push({ id, name, color: colorString });
        });
        //console.log('statusArray', statusArray);
        //statusArray.unshift({ id: 'All states', name: 'All states', color: 'black', types: [] });
        //setActivityStatusList(statusArray);
        activityStatusList.current = statusArray;
        let filterAvailableStates = filterParams?.FILTER.COL.filter((col: any) => col.name === 'customState');
        setStateSelectedKey(filterParams ? new Set(filterAvailableStates[0]?.value?.split(',')) : new Set(['All states']));
      }
    } finally {
      setShowLoader(false);
    }
  }, [selectedApp]);

  const handleSelectionChange = (value: any) => {
    //console.log('value', value);
    const arrValue = Array.from(value);
    const allStatesCheck = arrValue.includes('All states') && arrValue.indexOf('All states') !== 0;

    if (allStatesCheck) {
      const allStatesSet = new Set(['All states']);
      setStateSelectedKey(allStatesSet);
      createParams('customState', allStatesSet);
    } else {
      const filteredValue = arrValue.filter(item => item !== 'All states');
      const updateValue = filteredValue.length > 0 ? new Set(filteredValue) : new Set(['All states']);
      setStateSelectedKey(new Set(Array.from(updateValue).map(String)));
      createParams('customState', updateValue);
    }
  };

  if (showLoader) {
    return (
      <Flex width="100%" height="100%" UNSAFE_style={{ backgroundColor: 'white' }} justifyContent={'center'} alignContent={'center'} alignItems={'center'}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  } else {
    return (
      <Flex direction={'column'}  alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'}>
        <Flex direction={'column'} gap={'4px'} alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'} UNSAFE_className={styles.activity_status_container}>
          <Flex direction={'row'} height={'36px'} alignSelf={'stretch'} alignItems={'center'} justifyContent={'space-between'} UNSAFE_className={styles.activity_status_heading}>
            <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
              <View position={'relative'} UNSAFE_className={styles.activity_status_heading_text}>
                {t('activity_status', { ns: 'layout_components' })}
              </View>
            </Flex>

            {toggleListVisibility.stateList ? (
              <ChevronDown color="#B0BEC5" onClick={() => setToggleListVisibility((preValue: any) => ({ ...preValue, stateList: false }))} style={{ cursor: 'pointer' }} />
            ) : (
              <ChevronUp color="#B0BEC5" onClick={() => setToggleListVisibility((preValue: any) => ({ ...preValue, stateList: true }))} style={{ cursor: 'pointer' }} />
            )}
          </Flex>
          <Flex
            direction={'column'}
            alignSelf={'stretch'}
            alignItems={'start'}
            justifyContent={'start'}
            UNSAFE_className={isMobile ? styles.activity_status_component_mobile : styles.activity_status_component}
            UNSAFE_style={{ display: toggleListVisibility.stateList ? '' : 'none' }}
          >
            <ListBox
              id="listActivityStatus"
              width="100%"
              selectionMode="multiple"
              defaultSelectedKeys={stateSelectedKey}
              selectedKeys={stateSelectedKey}
              items={activityStatusList.current}
              onSelectionChange={(value: any) => {
                handleSelectionChange(value);
              }}
              UNSAFE_style={{ color: '#455a64 !important' }}
              aria-label="none"
            >
              {(item: any) => (
                <Item key={item.id} textValue={truncateText(item.name, 18)}>
                  <Flex direction={'row'} height={'30px'} UNSAFE_style={{ paddingBottom: '30px' }}>
                    <View>
                      <CircleFill style={{ paddingRight: '10px', paddingTop: '6px' }} size={'18px'} color={item.color} />
                    </View>
                    <View UNSAFE_style={{ whiteSpace: 'nowrap', color: '#455a64 !important', lineHeight: '30px' }}>
                      <span title={item.name}>{truncateText(item.name, 18)}</span>
                    </View>
                  </Flex>
                </Item>
              )}
            </ListBox>
            <View position={'relative'} width={'100%'} height={'20px'} UNSAFE_className={styles.activity_status_clear_all_container}>
              <View position={'relative'} UNSAFE_className={styles.activity_status_clear_all_text}>
                <Link onPress={() => handleSelectionChange(new Set(['All states']))}>{t('clear_all', { ns: 'layout_components' })}</Link>
              </View>
            </View>
          </Flex>
        </Flex>
      </Flex>
    );
  }
};

export default ActivityStatusFilterComponent;
